import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ReactNode
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";
import { useRouteState } from "@/hooks/useRouteState";
import { GetWSMgr } from "@/Utils/StateLib";
import { DISPLAY_MODES, SEARCH_PARAM_DISPLAY_KEY, enumUISubModes } from "@coreapp/constants";

interface TabularDisplayContextType {
  displayMode: string;
  changeDisplayMode: (mode: string) => void;
  isPositionDoc: boolean;
  shouldShowDisplayToggle: boolean;
  isTabularDisplay: boolean;
  isDirectoryDisplay: boolean;
  focusRestoreRequestedRef: React.MutableRefObject<boolean>;
}

const TabularDisplayContext = createContext<TabularDisplayContextType | undefined>(undefined);

interface TabularDisplayProviderProps {
  children: ReactNode;
}

const TabularDisplayProvider: React.FC<TabularDisplayProviderProps> = ({ children }) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { chartDocument } = useChartDocumentContext();

  const isPositionDoc = useMemo<boolean>(
    () => chartDocument?.state?.IsPositionDocument?.() === true,
    [chartDocument]
  );

  const displayMode = useMemo<string>(() => {
    const paramView = searchParams.get(SEARCH_PARAM_DISPLAY_KEY);
    const validModes: string[] = Object.values(DISPLAY_MODES);
    return paramView && validModes.includes(paramView) ? paramView : DISPLAY_MODES.CHART;
  }, [searchParams]);

  const { mode } = useRouteState();
  const isPlanningRoute: boolean = mode === "planning";

  const focusRestoreRequestedRef = useRef(false);

  // No unmount cleanup — the guard effect below handles stripping ?display=
  // on non-planning routes. An unmount cleanup with [] deps would capture a stale
  // setSearchParams from the initial mount, navigating to the wrong URL on crash.

  useEffect(() => {
    // Keep ?display= on planning routes (table view), when showing
    // directory overlay, and when view-manager is active. Only strip it
    // when none of these conditions apply — prevents stale ?display=table
    // from persisting after leaving planning.
    const currentDisplay = searchParams.get(SEARCH_PARAM_DISPLAY_KEY);
    const isDirectory = currentDisplay === DISPLAY_MODES.DIRECTORY;
    const isViewManager = currentDisplay === DISPLAY_MODES.VIEWMANAGER;

    if (isViewManager) {
      const wsMgr = GetWSMgr();
      const ctm = wsMgr?.GetChartTemplatesMgr?.();
      const viewParam = searchParams.get("view");
      const targetTemplate = viewParam
        ? (ctm?.FindTemplateByID?.(viewParam) ??
          ctm?.FindTemplateByIDAllSets?.(viewParam, true)?.template)
        : chartDocument?.state?.GetActiveTemplate?.();
      if (targetTemplate?.GetTemplateLibrary?.() === "system") {
        setSearchParams(params => {
          params.delete(SEARCH_PARAM_DISPLAY_KEY);
          return params;
        });
        return;
      }
    }

    if (isPlanningRoute || isDirectory || isViewManager) return;

    if (searchParams.has(SEARCH_PARAM_DISPLAY_KEY)) {
      setSearchParams(params => {
        params.delete(SEARCH_PARAM_DISPLAY_KEY);
        return params;
      });
    }
  }, [location, isPlanningRoute, searchParams, setSearchParams, chartDocument]);

  // URL → sub-mode sync for ?display=viewmanager. Mirrors the pattern
  // RightSidebarProvider uses for ?tool=background: URL is source of truth,
  // coreapp sub-mode is kept aligned by this effect so existing state
  // machine logic (fSubMode checks in QXWSMgr) continues to work.
  useEffect(() => {
    const wsMgr = GetWSMgr();
    if (!wsMgr) return;
    const currentDisplay = searchParams.get(SEARCH_PARAM_DISPLAY_KEY);
    const urlSaysViewManager = currentDisplay === DISPLAY_MODES.VIEWMANAGER;
    const inViewManager = wsMgr.GetSubMode?.() === enumUISubModes.viewmanager;
    if (urlSaysViewManager && !inViewManager) {
      wsMgr.SwitchSubMode?.(enumUISubModes.viewmanager);
    } else if (!urlSaysViewManager && inViewManager) {
      wsMgr.SwitchSubMode?.(enumUISubModes.standard);
    }
  }, [searchParams]);

  const changeDisplayMode = useCallback(
    (mode: string) => {
      const validModes: string[] = Object.values(DISPLAY_MODES);
      const normalizedMode = validModes.includes(mode) ? mode : DISPLAY_MODES.CHART;
      if (normalizedMode === displayMode) return;

      focusRestoreRequestedRef.current = true;

      setSearchParams(params => {
        if (normalizedMode !== DISPLAY_MODES.CHART) {
          params.set(SEARCH_PARAM_DISPLAY_KEY, normalizedMode);
        } else if (params.has(SEARCH_PARAM_DISPLAY_KEY)) {
          params.delete(SEARCH_PARAM_DISPLAY_KEY);
        }
        return params;
      });
    },
    [displayMode, setSearchParams]
  );

  const shouldShowDisplayToggle: boolean = isPlanningRoute && isPositionDoc;

  const isTabularDisplay: boolean = displayMode === DISPLAY_MODES.TABLE;
  const isDirectoryDisplay: boolean = displayMode === DISPLAY_MODES.DIRECTORY;

  const contextValue = useMemo<TabularDisplayContextType>(
    () => ({
      displayMode,
      changeDisplayMode,
      isPositionDoc,
      shouldShowDisplayToggle,
      isTabularDisplay,
      isDirectoryDisplay,
      focusRestoreRequestedRef
    }),
    [
      displayMode,
      changeDisplayMode,
      isPositionDoc,
      shouldShowDisplayToggle,
      isTabularDisplay,
      isDirectoryDisplay,
      focusRestoreRequestedRef
    ]
  );

  return (
    <TabularDisplayContext.Provider value={contextValue}>{children}</TabularDisplayContext.Provider>
  );
};

export const useTabularDisplayContext = (): TabularDisplayContextType => {
  const context = useContext(TabularDisplayContext);
  if (context === undefined) {
    throw new Error("useTabularDisplayContext must be used within a TabularDisplayProvider");
  }
  return context;
};

export const useOptionalTabularDisplayContext = (): TabularDisplayContextType | undefined => {
  return useContext(TabularDisplayContext);
};

export default TabularDisplayProvider;
